import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import ImageUpload from '../ui/ImageUpload';
import {
  Plus,
  Pencil,
  Trash2,
  Save,
  X,
  Users,
  GripVertical,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  name: '',
  role_sl: '',
  role_en: '',
  phone: '',
  image: '',
  birth_date: '',
  gender: '',
  join_date: '',
  order: 0,
};

const FIELD_LABELS = {
  name: 'Ime in priimek',
  role_sl: 'Vloga (SL)',
  role_en: 'Vloga (EN)',
  phone: 'Telefon',
  image: 'Profilna slika',
  birth_date: 'Rojstni datum',
  gender: 'Spol',
  join_date: 'Datum vstopa v LD',
};

const formatValue = (key, value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (key === 'gender') {
    if (value === 'M') return 'Moški';
    if (value === 'Ž') return 'Ženski';
  }
  if (key === 'image') {
    return value.length > 40 ? `${value.slice(0, 40)}…` : value;
  }
  return String(value);
};

const AdminLeadership = () => {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [originalData, setOriginalData] = useState(null);
  const [pendingChanges, setPendingChanges] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      const response = await axios.get(`${API}/leadership`);
      setMembers(response.data);
    } catch (error) {
      console.error('Error fetching leadership:', error);
      if (error.response?.status === 404 || (Array.isArray(error.response?.data) && error.response.data.length === 0)) {
        await seedLeadership();
      }
    } finally {
      setLoading(false);
    }
  };

  const seedLeadership = async () => {
    try {
      await axios.post(`${API}/seed-leadership`);
      await fetchMembers();
      toast.success('Podatki vodstva so bili uvoženi');
    } catch (error) {
      console.error('Error seeding leadership:', error);
    }
  };

  const computeDiff = (original, current) => {
    const trackedKeys = ['name', 'role_sl', 'role_en', 'phone', 'image', 'birth_date', 'gender', 'join_date'];
    const changes = [];
    trackedKeys.forEach((key) => {
      const before = original?.[key] ?? '';
      const after = current?.[key] ?? '';
      if (String(before) !== String(after)) {
        changes.push({ key, before, after });
      }
    });
    return changes;
  };

  const persistMember = async (payload) => {
    try {
      if (editingId) {
        await axios.put(`${API}/leadership/${editingId}`, payload);
        toast.success('Član je bil posodobljen');
      } else {
        const newOrder = members.length + 1;
        await axios.post(`${API}/leadership`, { ...payload, order: newOrder });
        toast.success('Nov član je bil dodan');
      }
      resetForm();
      fetchMembers();
    } catch (error) {
      console.error('Error saving member:', error);
      toast.error('Napaka pri shranjevanju');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (editingId && originalData) {
      const changes = computeDiff(originalData, formData);
      if (changes.length === 0) {
        toast.info('Ni nobenih sprememb za shraniti');
        return;
      }
      setPendingChanges(changes);
      return;
    }

    await persistMember(formData);
  };

  const handleConfirmChanges = async () => {
    setPendingChanges(null);
    await persistMember(formData);
  };

  const handleEdit = (member) => {
    const data = {
      name: member.name || '',
      role_sl: member.role_sl || '',
      role_en: member.role_en || '',
      phone: member.phone || '',
      image: member.image || '',
      birth_date: member.birth_date || '',
      gender: member.gender || '',
      join_date: member.join_date || '',
      order: member.order || 0,
    };
    setEditingId(member.id);
    setFormData(data);
    setOriginalData(data);
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati tega člana?')) {
      return;
    }

    try {
      await axios.delete(`${API}/leadership/${id}`);
      toast.success('Član je bil izbrisan');
      fetchMembers();
    } catch (error) {
      console.error('Error deleting member:', error);
      toast.error('Napaka pri brisanju');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setShowAddForm(false);
    setFormData(EMPTY_FORM);
    setOriginalData(null);
    setPendingChanges(null);
  };

  const getInitials = (name) => name.split(' ').map((n) => n[0]).join('');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-emerald-400" />
          <h2 className="text-xl font-semibold text-white">Vodstvo in člani</h2>
          <span className="px-2 py-1 text-xs bg-stone-800 text-stone-400 rounded-full">
            {members.length} članov
          </span>
        </div>
        {!showAddForm && (
          <Button
            data-testid="add-member-btn"
            onClick={() => {
              setFormData(EMPTY_FORM);
              setOriginalData(null);
              setEditingId(null);
              setShowAddForm(true);
            }}
            className="bg-emerald-600 hover:bg-emerald-500"
          >
            <Plus className="w-4 h-4 mr-2" />
            Dodaj člana
          </Button>
        )}
      </div>

      {/* Add/Edit Form */}
      {showAddForm && (
        <Card className="bg-stone-800/50 border-stone-700/50 p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {editingId ? 'Uredi člana' : 'Dodaj novega člana'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="member-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Ime in priimek</Label>
                <Input
                  data-testid="member-name-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="Janez Novak"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Telefon</Label>
                <Input
                  data-testid="member-phone-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="041 123 456"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Vloga (slovensko)</Label>
                <Input
                  data-testid="member-role-sl-input"
                  value={formData.role_sl}
                  onChange={(e) => setFormData({ ...formData, role_sl: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="npr. Predsednik"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Vloga (angleško)</Label>
                <Input
                  data-testid="member-role-en-input"
                  value={formData.role_en}
                  onChange={(e) => setFormData({ ...formData, role_en: e.target.value })}
                  required
                  className="bg-stone-900/50 border-stone-700 text-white"
                  placeholder="npr. Chairman"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label className="text-stone-300">Rojstni datum</Label>
                <Input
                  data-testid="member-birth-date-input"
                  type="date"
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Spol</Label>
                <Select
                  value={formData.gender || undefined}
                  onValueChange={(val) => setFormData({ ...formData, gender: val })}
                >
                  <SelectTrigger
                    data-testid="member-gender-select"
                    className="bg-stone-900/50 border-stone-700 text-white"
                  >
                    <SelectValue placeholder="Izberi spol" />
                  </SelectTrigger>
                  <SelectContent className="bg-stone-900 border-stone-700 text-white">
                    <SelectItem value="M">Moški</SelectItem>
                    <SelectItem value="Ž">Ženski</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-stone-300">Datum vstopa v LD</Label>
                <Input
                  data-testid="member-join-date-input"
                  type="date"
                  value={formData.join_date}
                  onChange={(e) => setFormData({ ...formData, join_date: e.target.value })}
                  className="bg-stone-900/50 border-stone-700 text-white"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-stone-300">Profilna slika (neobvezno)</Label>
              <ImageUpload
                value={formData.image}
                onChange={(url) => setFormData({ ...formData, image: url })}
                previewClassName="w-24 h-24 rounded-full mx-auto"
              />
              <p className="text-xs text-stone-500 text-center">
                Naložite sliko ali pustite prazno za prikaz začetnic
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                data-testid="submit-member-btn"
                type="submit"
                className="bg-emerald-600 hover:bg-emerald-500"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingId ? 'Shrani spremembe' : 'Dodaj člana'}
              </Button>
              <Button
                data-testid="cancel-member-btn"
                type="button"
                variant="outline"
                onClick={resetForm}
                className="border-stone-700 text-stone-300 hover:bg-stone-800"
              >
                <X className="w-4 h-4 mr-2" />
                Prekliči
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Members List */}
      <div className="grid gap-4">
        {members.length === 0 ? (
          <Card className="bg-stone-800/50 border-stone-700/50 p-8 text-center">
            <Users className="w-12 h-12 text-stone-600 mx-auto mb-4" />
            <p className="text-stone-400">Ni članov vodstva</p>
            <Button
              onClick={seedLeadership}
              className="mt-4 bg-emerald-600 hover:bg-emerald-500"
            >
              Uvozi privzete podatke
            </Button>
          </Card>
        ) : (
          members.map((member) => (
            <Card
              key={member.id}
              className="bg-stone-800/50 border-stone-700/50 p-4 hover:border-emerald-600/30 transition-colors"
              data-testid={`member-card-${member.id}`}
            >
              <div className="flex items-center gap-4">
                <div className="text-stone-600 cursor-move">
                  <GripVertical className="w-5 h-5" />
                </div>

                <Avatar className="w-14 h-14 ring-2 ring-stone-700">
                  {member.image ? (
                    <AvatarImage src={member.image} alt={member.name} />
                  ) : null}
                  <AvatarFallback className="bg-gradient-to-br from-emerald-700 to-emerald-900 text-white text-lg font-bold">
                    {getInitials(member.name)}
                  </AvatarFallback>
                </Avatar>

                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-semibold">{member.name}</h4>
                  <p className="text-emerald-400 text-sm">{member.role_sl}</p>
                  <p className="text-stone-500 text-sm">{member.phone}</p>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1 text-xs text-stone-500">
                    {member.birth_date && (
                      <span>Rojen/a: {member.birth_date}</span>
                    )}
                    {member.gender && (
                      <span>Spol: {member.gender === 'M' ? 'Moški' : 'Ženski'}</span>
                    )}
                    {member.join_date && (
                      <span>Vstop v LD: {member.join_date}</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    data-testid={`edit-member-btn-${member.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleEdit(member)}
                    className="border-stone-700 text-stone-300 hover:bg-stone-800"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    data-testid={`delete-member-btn-${member.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(member.id)}
                    className="border-red-900/50 text-red-400 hover:bg-red-900/20"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </Card>
          ))
        )}
      </div>

      {/* Confirmation Dialog for edits */}
      <AlertDialog open={!!pendingChanges} onOpenChange={(open) => !open && setPendingChanges(null)}>
        <AlertDialogContent className="bg-stone-900 border-stone-700 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Potrditev sprememb</AlertDialogTitle>
            <AlertDialogDescription className="text-stone-400">
              Preverite spremembe pred shranjevanjem. Posodobljena bodo le spremenjena polja.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-72 overflow-y-auto space-y-2 text-sm" data-testid="changes-diff-list">
            {pendingChanges?.map((change) => (
              <div
                key={change.key}
                className="bg-stone-800/60 border border-stone-700/60 rounded-md px-3 py-2"
              >
                <div className="text-emerald-400 text-xs font-semibold mb-1">
                  {FIELD_LABELS[change.key] || change.key}
                </div>
                <div className="text-stone-300">
                  <span className="line-through text-red-400/80 break-words">
                    {formatValue(change.key, change.before)}
                  </span>
                  <span className="mx-2 text-stone-500">→</span>
                  <span className="text-emerald-300 break-words">
                    {formatValue(change.key, change.after)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="cancel-changes-btn"
              className="bg-stone-800 border-stone-700 text-stone-300 hover:bg-stone-700"
            >
              Prekliči
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-changes-btn"
              onClick={handleConfirmChanges}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              Potrdi in shrani
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminLeadership;
